import { CalendarTodayOutlined } from '@mui/icons-material'
import type { SelectChangeEvent } from '@mui/material'
import { Divider, Popover, Stack, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'

import CustomButton from '@/components/inputs/CustomButton'
import type { SelectFieldOption } from '@/components/inputs/CustomSelectField'
import CustomSelectField from '@/components/inputs/CustomSelectField'
import type {
  ResourceSetDateField,
  ResourceSetDateRange,
  ResourceSetFilters,
} from '@/features/prequalification/localControl/utils/resourceSetFilters'

type LocalPqResourceSetsFiltersPopoverProps = {
  open: boolean
  anchorEl: HTMLElement | null
  onClose: () => void
  filters: ResourceSetFilters
  onFiltersChange: (filters: ResourceSetFilters) => void
}

const DATE_RANGES: ResourceSetDateRange[] = ['all', 'last_24h', 'last_7d', 'last_30d', 'last_90d']
const DATE_FIELDS: ResourceSetDateField[] = ['created', 'updated']

// Render the dropdown value + options at the list's row text size (body2, 14px)
// instead of the 16px default, so the filters read as light as the resource set rows.
// Set on the Select root rather than `& .MuiSelect-select`: CustomSelectField
// overrides that selector for icon-bearing fields (the date field), which would
// drop the size — but the root font-size still cascades to the value text.
const COMPACT_VALUE_SX = { fontSize: '0.875rem' }
const COMPACT_MENU_PROPS = { sx: { '& .MuiMenuItem-root': { fontSize: '0.875rem' } } }

/**
 * The "Filters" popover anchored to the toolbar's Filters button. Holds the
 * less-frequent filters (date range) so the always-visible toolbar stays
 * uncluttered. Changes apply live to the list; Done just dismisses and Reset
 * clears these popover-scoped fields back to their defaults.
 */
function LocalPqResourceSetsFiltersPopover({
  open,
  anchorEl,
  onClose,
  filters,
  onFiltersChange,
}: Readonly<LocalPqResourceSetsFiltersPopoverProps>) {
  const { t } = useTranslation()

  const dateFieldOptions: SelectFieldOption<ResourceSetDateField>[] = DATE_FIELDS.map((field) => ({
    id: `date-field-${field}`,
    value: field,
    label: t(`prequalifications.list.filters.date_field.${field}`),
    icon: <CalendarTodayOutlined fontSize="small" sx={{ color: 'text.secondary' }} />,
  }))

  const dateRangeOptions: SelectFieldOption<ResourceSetDateRange>[] = DATE_RANGES.map((range) => ({
    id: `date-range-${range}`,
    value: range,
    label: t(`prequalifications.list.filters.date_range.${range}`),
  }))

  const handleReset = () => onFiltersChange({ ...filters, dateField: 'created', dateRange: 'all' })

  return (
    <Popover
      anchorEl={anchorEl}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      open={open}
      slotProps={{ paper: { sx: { mt: 1, width: 300, borderRadius: 2 } } }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      onClose={onClose}
    >
      <Stack sx={{ gap: 2.5, p: 2.5 }}>
        <Typography
          color="text.secondary"
          sx={{ fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase' }}
          variant="caption"
        >
          {t('prequalifications.list.filters.title')}
        </Typography>

        <CustomSelectField<ResourceSetDateField>
          fullWidth
          MenuProps={COMPACT_MENU_PROPS}
          id="resource-set-filter-date-field"
          label={t('prequalifications.list.filters.date_field_label')}
          options={dateFieldOptions}
          size="small"
          sx={COMPACT_VALUE_SX}
          value={filters.dateField}
          onChange={(event: SelectChangeEvent<ResourceSetDateField>) =>
            onFiltersChange({ ...filters, dateField: event.target.value as ResourceSetDateField })
          }
        />

        <CustomSelectField<ResourceSetDateRange>
          fullWidth
          MenuProps={COMPACT_MENU_PROPS}
          id="resource-set-filter-date-range"
          label={t('prequalifications.list.filters.date_range_label')}
          options={dateRangeOptions}
          size="small"
          sx={COMPACT_VALUE_SX}
          value={filters.dateRange}
          onChange={(event: SelectChangeEvent<ResourceSetDateRange>) =>
            onFiltersChange({ ...filters, dateRange: event.target.value as ResourceSetDateRange })
          }
        />

        <Divider sx={{ mt: -0.5 }} />

        <Stack sx={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
          <CustomButton color="primary" variant="text" onClick={handleReset}>
            {t('prequalifications.list.filters.reset')}
          </CustomButton>
          <CustomButton color="primary" variant="contained" onClick={onClose}>
            {t('prequalifications.list.filters.done')}
          </CustomButton>
        </Stack>
      </Stack>
    </Popover>
  )
}

export default LocalPqResourceSetsFiltersPopover
